import { motion } from 'framer-motion'
import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { ToolTip } from '../../../../components/toolTip/ToolTip'
import { translations } from '../../../../configs/translations'
import RaceService from '../../../../services/RaceService'
import { useStore } from '../../../../store/store'
import { arabNumberToRoman } from '../../../../utils/arabNumberToRoman'
import { formatTextWithParams } from '../../../../utils/formatTextWithParams'
import { getTimeFormated } from '../../../../utils/getTimeFormated'
import { LoaderIcon } from '../../../preloader/LoaderIcon/LoaderIcon'
import './SearchWithGhostOffer.css'

export interface GhostRideOffer {
	userName: string
	vehicle: string
	rating: string | null
	rank: number
	map: string
	duration: number
	diffDuration: number | null
}

export interface SearchWithGhostOfferProps {
	offer: GhostRideOffer
	playersByRankAndType: number
	withCancelButton?: boolean
	onCancelSearchClick: () => void
	onClose: () => void
}

const formatDuration = (millis: number) =>
	`${getTimeFormated(millis / 3600000, (millis / 60000) % 60, (millis / 1000) % 60)},${String(millis % 1000).padStart(3, '0')}`

export const SearchWithGhostOffer = ({ offer, playersByRankAndType, withCancelButton, onCancelSearchClick, onClose }: SearchWithGhostOfferProps) => {
	const language = useStore(state => state.language) as keyof typeof translations
	const t = translations[language]
	const [infoPos, setInfoPos] = useState({ x: 0, y: 0 })
	const [isInfoShown, setIsInfoShown] = useState(false)

	const handleError = (error: any) => {
		toast.error(
			error?.response?.data && t[error.response.data as keyof typeof t]
				? t[error.response.data as keyof typeof t]
				: t.error_unknown,
			{ className: "notification-error" }
		)
	}

	const accept = () => {
		RaceService.acceptGhostOffer().catch(handleError)
	}

	const cancel = () => {
		RaceService.cancelGhostOffer()
			.then(onClose)
			.catch(handleError)
	}

	const diffSecs = offer.diffDuration != null
		? (Math.abs(offer.diffDuration) / 1000).toFixed(1).replace('.', ',')
		: null

	return (
		<motion.div
			className='search-race-ghost-offer'
			initial={{ x: '120%', opacity: 0 }}
			animate={{ x: 0, opacity: 1 }}
			exit={{ x: '120%', opacity: 0 }}
			transition={{ type: 'spring', stiffness: 80, damping: 18 }}
		>
			{withCancelButton &&
				<div
					className='search-race-cancel-button'
					onClick={onCancelSearchClick}
				>
					<img src="./icons/32px/cancel.svg" alt="cancel" />
					<p>{t.cancel_search}</p>
				</div>
			}
			<div className="search-race-ghost-offer-content">

				<div className="search-race-ghost-offer-content-header">
					<LoaderIcon
						width={'23px'}
						height={'23px'}
					/>

					<div className='search-race-ghost-offer-conent-header-content'>
						<div className='search-race-ghost-offer-content-header-title'>
							<p>{`${t.race_search_is_still_in_progress}...`}</p>
						</div>

						<div className='search-race-ghost-offer-content-header-subtitle'>
							<p>{t.players_in_search_by_rank_and_car}: <span className='ghost-offer-text'>{playersByRankAndType}</span></p>
						</div>
					</div>
				</div>

				<div className='search-race-ghost-offer-content-body'>

					<div className='search-race-ghost-offer-content-body-header'>
						<div className='search-race-ghost-offer-content-body-header-title'>
							<div
								className='search-race-ghost-offer-content-body-header-title-icon'
								onMouseMove={e => { setInfoPos({ x: e.clientX, y: e.clientY }); setIsInfoShown(true) }}
								onMouseLeave={() => setIsInfoShown(false)}
							>
								<img src='../icons/ghost-info.svg' />
							</div>
							<ToolTip
								style='left'
								position={infoPos}
								content={t.ghost_race_info}
								isShown={isInfoShown}
							/>
							<p>{t.ghost_race_has_been_found}!</p>
						</div>
						<div className='search-ghost-offer-header-close' onClick={cancel}>
							<img src='../icons/close.png' />
						</div>
					</div>

					<div className='ghost-offer-body'>

						<div className='ghost-offer-ghost-info'>
							<div className='ghost-offer-ghost-name'>
								<p>{offer.userName}</p>
								{offer.rating != null &&
									<div className='ghost-offer-ghost-rank'>
										<p>{offer.rating}</p>
									</div>
								}
							</div>

							<div className='ghost-offer-ghost-car'>
								<p>{t.ghost_offer_vehicle}: <span className='ghost-offer-text'>{offer.vehicle} ({t.rank} {arabNumberToRoman(offer.rank)})</span></p>
							</div>

							<div className='ghost-offer-map'>
								<p>{t.ghost_offer_map}: <span className='ghost-offer-text'>{offer.map}</span></p>
							</div>
						</div>

						<div className='ghost-offer-ride-info'>
							<div className='ghost-offer-ride-duration'>
								<p>{formatDuration(offer.duration)}</p>
							</div>

							{diffSecs != null &&
								<div className='ghost-offer-ride-duration-difference'>
									<p>{formatTextWithParams(
										offer.diffDuration! >= 0 ? t.ghost_offer_slower_than_you : t.ghost_offer_faster_than_you,
										[diffSecs]
									)}</p>
								</div>
							}
						</div>

					</div>

					<div className='ghost-offer-play-button-container'>

						<div className='ghost-offer-play-button' onClick={accept}>
							<p>{t.start}</p>
						</div>

						<p className='ghost-offer-unranked-info'>{t.ghost_offer_without_rating_change}</p>

					</div>
				</div>

			</div>
		</motion.div>
	)
}
